import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import styled, { keyframes, css } from "styled-components";
import { REQUIRED_HEADERS_CONFIG } from "../constants";
import {
  RowContainer,
  ChecksPassedParagraph,
  FileNameParagraphNonFileName,
  FileNameParagraphFileName,
} from "./SuccessContainer";
import {
  RowContainerJustifyCenter,
  ProgressBarContainer,
  PercentageInformationText,
} from "./LoaderContainer";
import uploadIcon from "../assets/upload_icon.svg";

const Container = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  border-radius: 5px;
  background-color: transparent;
  flex-direction: column;
`;
// Define keyframes for the growing animation
const grow = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.15);
  }
  100% {
    transform: scale(1);
  }
`;

const move = keyframes`
  0% {
    background-position: 0 0;
  }
  100% {
    background-position: 50px 50px;
  }
`;

const ProgressFill = styled.div`
  height: 100%;
  background-color: #6285ff;
  transition: width 0.5s ease-in-out;
  border-radius: 10px;
  width: ${(props) => props.progress}%;

  // Apply background image only when animating
  ${(props) =>
    props.isAnimating &&
    css`
      background-image: linear-gradient(
        -45deg,
        rgba(255, 255, 255, 0.2) 25%,
        transparent 25%,
        transparent 50%,
        rgba(255, 255, 255, 0.2) 50%,
        rgba(255, 255, 255, 0.2) 75%,
        transparent 75%,
        transparent
      );
      background-size: 50px 50px;
      animation: ${move} 2s linear infinite;
    `}
`;

export const ImageContainer = styled.div`
  width: 100px; // set to the desired width
  height: 100px; // set to the desired height
  overflow: hidden;
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  animation: ${(props) =>
    props.grow
      ? css`
          ${grow} 0.8s linear infinite
        `
      : "none"};
`;

export const TextCenteredRowContainer = styled(RowContainer)`
  display: flex;
  text-align: center;
  width: 75%;
`;

export const SmallParagraph = styled(FileNameParagraphNonFileName)`
  font-size: 11px;
  font-style: italic;
`;

const BoldAction = styled(FileNameParagraphFileName)`
  text-decoration: ${(props) => (props.shouldUnderline ? "underline" : "none")};
`;

export const Image = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const styleSheet = {
  descriptionRowContainer: {
    marginTop: "-12px",
  },
};

const displayColumnHeaders = (requiredHeadersConfigObj) => {
  return `Column Headers: ${requiredHeadersConfigObj.columnHeaders}`;
};

export default function FileDropBox({ processing, accordionId }) {
  const [isHovered, setIsHovered] = useState(false);
  const [dots, setDots] = useState(0);
  const maxDots = 3;
  const requiredHeadersConfigObj = REQUIRED_HEADERS_CONFIG[accordionId - 1];

  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prevDots) => (prevDots + 1) % (maxDots + 1));
    }, 400); // Change the interval duration as needed
    return () => clearInterval(interval);
  }, []);

  const loadingDots = ".".repeat(dots);
  const nonBreakingSpaces = "\u00A0".repeat(maxDots - dots);
  /**
   * Generates content for different elements based on the processing state.
   *
   * @param {Object} processing - An object representing the processing state, including flags and data.
   * @param {boolean} processing.isProcessing - A flag indicating if processing is ongoing.
   * @param {string} processing.action - A string representing the action being performed.
   * @param {number} processing.progress - A number representing the progress of the processing (0-1).
   * @returns {Object} An object mapping element IDs to their respective content based on processing state.
   */
  const contentByElementId = {
    element1: processing.isProcessing
      ? "your file is \u00A0"
      : "Drag and drop your document or\u00A0",
    element2: processing.isProcessing
      ? `${processing.action}${loadingDots}${nonBreakingSpaces}`
      : "look for it",
    element3: processing.isProcessing ? (
      <RowContainerJustifyCenter>
        <ProgressBarContainer
          style={{ marginTop: "10px", marginBottom: "10px" }}
        >
          <ProgressFill
            progress={processing.progress * 100 || 0}
            isAnimating={processing.action === "testing"}
          />
        </ProgressBarContainer>
        <PercentageInformationText style={{ alignItems: "flex-start" }}>{`${
          Math.round(processing.progress * 100) || 0
        }%`}</PercentageInformationText>
      </RowContainerJustifyCenter>
    ) : (
      <RowContainer>
        <FileNameParagraphNonFileName>
          Compatible formats: XLS, XLSX
        </FileNameParagraphNonFileName>
      </RowContainer>
    ),
  };

  const handleDragEnter = (event) => {
    event.preventDefault();
    setIsHovered(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsHovered(false);
  };

  const handleDragOver = (event) => {
    event.preventDefault(); // Necessary to allow drop
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsHovered(false);
  };
  return (
    <Container
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <ImageContainer grow={isHovered}>
        <Image src={uploadIcon} alt="file upload icon" />
      </ImageContainer>
      <RowContainer style={styleSheet.descriptionRowContainer}>
        <ChecksPassedParagraph>
          {contentByElementId.element1}
          <BoldAction shouldUnderline={!processing.isProcessing}>
            {contentByElementId.element2}
          </BoldAction>
        </ChecksPassedParagraph>
      </RowContainer>
      {contentByElementId.element3}
      <TextCenteredRowContainer>
        <FileNameParagraphNonFileName>
          {displayColumnHeaders(requiredHeadersConfigObj)}
        </FileNameParagraphNonFileName>
      </TextCenteredRowContainer>
      {requiredHeadersConfigObj.info && (
        <TextCenteredRowContainer>
          <SmallParagraph>{requiredHeadersConfigObj.info}</SmallParagraph>
        </TextCenteredRowContainer>
      )}
    </Container>
  );
}

FileDropBox.propTypes = {
  file: PropTypes.oneOfType([
    PropTypes.shape({
      lastModified: PropTypes.number,
      name: PropTypes.string,
      size: PropTypes.number,
      type: PropTypes.string,
    }),
    PropTypes.instanceOf(null),
  ]),
  processing: PropTypes.shape({
    isProcessing: PropTypes.bool,
    action: PropTypes.string,
    progress: PropTypes.number,
  }),
  accordionId: PropTypes.number.isRequired,
};
